/* ═══════════════════════════════════════════════════════════════
   Pure glue between `module-categories.ts` (shared/ — the rules) and
   `ModuleBrowser` (components/ — the UI): column values for the Category
   and Tags columns, their filter options, the layout numbers (rail width,
   compact threshold), and the "type a path to create it" label.

   Zero React, zero DOM — every function here is a straight data
   transform, so the browser's navigation and the table's search/filter/
   group all read the SAME string a cell renders, and that string is
   proven once in a test rather than three times by hand.
   ═══════════════════════════════════════════════════════════════ */

import type { SpecModule } from '../../shared/spec-modules';
import {
  categoryPathLabel,
  moduleEffectiveCategoryId,
  parseCategoryPath,
  type ModuleCategory,
} from '../../shared/module-categories';

/** The Category column's cell value: the full path label, or '' for
 *  uncategorized (including a module whose stored id is dangling). */
export function categoryColumnValue(
  module: Pick<SpecModule, 'categoryId'>,
  categories: ModuleCategory[]
): string {
  const eff = moduleEffectiveCategoryId(module, categories);
  return eff ? categoryPathLabel(eff, categories) : '';
}

/** The Tags column's cell value: every tag, comma-joined — what the
 *  table's built-in free-text search and the "contains" filter both read. */
export function tagsColumnValue(module: Pick<SpecModule, 'tags'>): string {
  return (module.tags || []).join(', ');
}

/** Every full path label, sorted, plus "Uncategorized" — the multiselect
 *  filter's option list for the Category column. One entry per category,
 *  so "is any of" matches exactly the strings `categoryColumnValue` can
 *  ever produce. */
export function categoryFilterOptions(
  categories: ModuleCategory[]
): { value: string; label: string }[] {
  const labels = (categories || []).map((c) => categoryPathLabel(c.id, categories)).filter(Boolean);
  const unique = [...new Set(labels)].sort((a, b) => a.localeCompare(b));
  return [
    ...unique.map((label) => ({ value: label, label })),
    { value: '', label: 'Uncategorized' },
  ];
}

// ─── Layout numbers (host-chosen, never a CSS width media query) ───

/** Below this measured container width, the host hands the browser the
 *  compact column set (name + tags) instead of the full one. Measured by
 *  the HOST (ResizeObserver on its own dock container), never by a CSS
 *  breakpoint. */
export const MODULE_BROWSER_COMPACT_BELOW_PX = 480;

export function moduleBrowserIsCompact(containerWidthPx: number): boolean {
  return containerWidthPx > 0 && containerWidthPx < MODULE_BROWSER_COMPACT_BELOW_PX;
}

/** The `rail` layout's category column width: a fraction of the
 *  container, floored and capped so it never collapses to nothing on a
 *  narrow window or swallows the table on a wide one. */
export function moduleBrowserRailWidthPx(
  containerWidthPx: number,
  fraction = 0.26,
  floorPx = 200,
  capPx = 320
): number {
  if (containerWidthPx <= 0) return floorPx;
  return Math.max(floorPx, Math.min(capPx, Math.round(containerWidthPx * fraction)));
}

/** How many sub-category entries the `stacked` layout shows before
 *  collapsing the rest behind "Show all (N)" — roughly three rows of a
 *  compact card grid. */
export const MODULE_BROWSER_STACKED_VISIBLE = 6;

// ─── Type-to-create label (the picker's own words, not a generic one) ───

/** "Create "Equipment › Division 27 › Speakers › QSC"" for a typed path,
 *  or `null` when there is nothing to offer (an empty/whitespace query). */
export function categoryCreateLabel(typed: string): string | null {
  const names = parseCategoryPath(typed);
  if (!names.length) return null;
  return `Create "${names.join(' › ')}"`;
}

// ─── Bulk action result → ONE toast message ───

/** One item's outcome in a sequential bulk loop (Set category / Add tags /
 *  Clear tags on the library). `error` absent ⇒ that item saved. */
export interface BulkOutcome {
  name: string;
  error?: string;
}

/** Turns a bulk loop's per-item outcomes into the ONE toast the caller
 *  shows: every item saved keeps the plain success phrasing; any failure
 *  names which items failed and says to try again — never an apology.
 *  Partial success states both halves so the user knows what actually
 *  landed before the list reloads under them. */
export function summarizeBulkResult(
  outcomes: BulkOutcome[],
  successMessage: (succeededCount: number) => string
): { message: string; isError: boolean } {
  const failed = outcomes.filter((o) => !!o.error);
  const succeededCount = outcomes.length - failed.length;
  if (failed.length === 0) return { message: successMessage(succeededCount), isError: false };

  const names = failed.map((f) => `"${f.name}"`).join(', ');
  const lead = succeededCount > 0 ? `${successMessage(succeededCount)}. ` : '';
  return {
    message: `${lead}${failed.length} module${failed.length === 1 ? '' : 's'} could not be saved — ${names}. Try again.`,
    isError: true,
  };
}
